import React, { createContext, useReducer, useCallback, useMemo, useRef, useEffect } from 'react';
import { TaskState, TaskContextType } from './TaskTypes';
import { RoutingTaskSummaryInfo } from '../../models/types';
import { taskSummaryService } from '../../api/services';

// Initial state for task context
const initialState: TaskState = {
  tasks: [],
  loading: false,
  error: null,
  searchTerm: '',
  sortConfig: {
    key: 'createdAt',
    direction: 'desc'
  }
};

// Define the action types
type TaskAction =
  | { type: 'FETCH_TASKS_START' }
  | { type: 'FETCH_TASKS_SUCCESS'; tasks: RoutingTaskSummaryInfo[] }
  | { type: 'FETCH_TASKS_FAIL'; error: string }
  | { type: 'SEARCH_TASKS'; searchTerm: string }
  | { type: 'SORT_TASKS'; key: keyof RoutingTaskSummaryInfo }
  | { type: 'CLEAR_ERROR' }
  | { type: 'UPDATE_TASK'; task: RoutingTaskSummaryInfo };

// Create the reducer function
const taskReducer = (state: TaskState, action: TaskAction): TaskState => {
  console.log('[TaskReducer] Action:', action.type, action);
  
  switch (action.type) {
    case 'FETCH_TASKS_START':
      return {
        ...state,
        tasks: [],
        loading: true,
        error: null
      };
    case 'FETCH_TASKS_SUCCESS':
      console.log('[TaskReducer] Fetched tasks:', action.tasks);
      console.log('[TaskReducer] Tasks length:', action.tasks.length);
      return {
        ...state,
        tasks: action.tasks,
        loading: false,
        error: null
      };
    case 'FETCH_TASKS_FAIL':
      return {
        ...state,
        loading: false,
        error: action.error
      };
    case 'SEARCH_TASKS':
      return {
        ...state,
        searchTerm: action.searchTerm
      };
    case 'SORT_TASKS':
      return {
        ...state,
        sortConfig: {
          key: action.key,
          direction: 
            state.sortConfig.key === action.key && 
            state.sortConfig.direction === 'asc' 
              ? 'desc' 
              : 'asc'
        }
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    case 'UPDATE_TASK': {
      const index = state.tasks.findIndex(task => task.taskId === action.task.taskId);
      if (index === -1) {
        return state;
      }
      const updatedTasks = [...state.tasks];
      updatedTasks[index] = action.task;
      return {
        ...state,
        tasks: updatedTasks
      };
    }
    default:
      return state;
  }
};

// Create the context with a default value
export const TaskContext = createContext<TaskContextType>({
  ...initialState,
  fetchTasks: async () => {},
  searchTasks: () => {},
  sortTasks: () => {},
  clearError: () => {},
  getFilteredTasks: () => [],
  refreshTask: async () => {}
});

interface TaskProviderProps {
  children: React.ReactNode;
}

// Create the provider component
export const TaskProvider: React.FC<TaskProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(taskReducer, initialState);
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Fetch tasks method
  const fetchTasks = useCallback(async (hubId: string) => {
    const currentState = stateRef.current;
    console.log('[fetchTasks] Fetching tasks for hub:', hubId);
    console.log('[fetchTasks] Current state before fetch:', { 
      tasks: currentState.tasks.length, 
      loading: currentState.loading, 
      error: currentState.error 
    });
    
    if (!hubId) {
      console.log('[fetchTasks] No hubId provided, skipping fetch');
      return;
    }
    
    try {
      console.log('[fetchTasks] Dispatching FETCH_TASKS_START');
      // First clear any existing tasks and set loading state
    dispatch({ type: 'FETCH_TASKS_START' });
    
    // Clear existing tasks first when hub changes
    if (currentState.tasks.length > 0) {
      console.log('[fetchTasks] Clearing existing tasks before fetching new ones');
    }
      
      console.log('[fetchTasks] Calling taskSummaryService.getTasksForHub with hubId:', hubId);
      const tasksData = await taskSummaryService.getTasksForHub(hubId);
      
      console.log('[fetchTasks] Received tasks data:', tasksData);
      console.log('[fetchTasks] Tasks data type:', typeof tasksData);
      console.log('[fetchTasks] Is tasks array?', Array.isArray(tasksData));
      console.log('[fetchTasks] Tasks data length:', tasksData.length);
      
      if (Array.isArray(tasksData) && tasksData.length > 0) {
        console.log('[fetchTasks] Sample task item:', tasksData[0]);
      }
      
      console.log('[fetchTasks] Dispatching FETCH_TASKS_SUCCESS');
      dispatch({ type: 'FETCH_TASKS_SUCCESS', tasks: tasksData });
    } catch (error) {
      console.error('[fetchTasks] Failed to fetch tasks:', error);
      dispatch({ 
        type: 'FETCH_TASKS_FAIL', 
        error: 'Failed to load tasks. Please try again later.' 
      });
    }
  }, []);

  // Search tasks method
  const searchTasks = useCallback((searchTerm: string) => {
    dispatch({ type: 'SEARCH_TASKS', searchTerm });
  }, []);

  // Sort tasks method
  const sortTasks = useCallback((key: keyof RoutingTaskSummaryInfo) => {
    dispatch({ type: 'SORT_TASKS', key });
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const refreshTask = useCallback(async (depotId: string, taskId: string) => {
    try {
      const taskSummary = await taskSummaryService.getTaskForHub(depotId, taskId);
      dispatch({ type: 'UPDATE_TASK', task: taskSummary });
    } catch (error) {
      console.error('[refreshTask] Failed to refresh task', error);
      throw error;
    }
  }, []);

  // Get filtered and sorted tasks
  const getFilteredTasks = useCallback(() => {
    console.log('[getFilteredTasks] Current state tasks length:', state.tasks.length);
    
    // Filter tasks based on search term
    const filteredTasks = state.tasks.filter(task => {
      const searchTerm = state.searchTerm.toLowerCase();
      return (
        task.taskId.toLowerCase().includes(searchTerm) ||
        task.createdBy.toLowerCase().includes(searchTerm) ||
        task.status.toLowerCase().includes(searchTerm)
      );
    });

    console.log('[getFilteredTasks] Filtered tasks length:', filteredTasks.length);

    // Sort tasks based on sort config
    const sortedTasks = [...filteredTasks].sort((a, b) => {
      const key = state.sortConfig.key;
      const aValue = a[key];
      const bValue = b[key];
      if (aValue == null || bValue == null) {
        return 0;
      }
      if (aValue < bValue) {
        return state.sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return state.sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    console.log('[getFilteredTasks] Returning sorted tasks length:', sortedTasks.length);
    return sortedTasks;
  }, [state.tasks, state.searchTerm, state.sortConfig]);

  // Context value
  const contextValue = useMemo<TaskContextType>(() => ({
    ...state,
    fetchTasks,
    searchTasks,
    sortTasks,
    clearError,
    getFilteredTasks,
    refreshTask
  }), [state, fetchTasks, searchTasks, sortTasks, clearError, getFilteredTasks, refreshTask]);

  return (
    <TaskContext.Provider value={contextValue}>
      {children}
    </TaskContext.Provider>
  );
}; 
